import { createFileRoute, redirect } from '@tanstack/react-router';
import Header from '@/components/Header/Header';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@/components/core/Button';
import Input from '@/components/core/Input';
import { Dropdown, OptionsType } from '@/components/core/Dropdown';
import { FilterPanel } from '@/components/FilterPanel';
import { useState, useMemo, useCallback, useEffect } from 'react';
import { Loading } from '@/components/Loading';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { FilteredListContainer } from '@/components/FilteredListContainer';
import { VirtualizedList } from '@/components/VirtualizedList.tsx';
import { RightPanel } from '@/components/RightPanel/RightPanel';
import { useAscOrDescIcon } from '@/hooks/useSort.tsx';
import { AdministratorListItem } from '@/components/Administrators/AdministratorListItem';
import { SearchFilterParams, SortDirection } from '@/types/filterSearch';
import { createSortConfig } from '@/utils/sort';
import { useSearchFilterSort } from '@/hooks/useSearchFilterSort';
import { ActionButtons } from '@/components/Header/ActionButtons';
import { IconPlus } from '@/icons/CoreIcons';
import { DialogWindow } from '@/components/Dialog';
import { AddAdministratorDialog } from '@/components/Administrators/AddAdministratorDialog';
import { ExtendedUserDTO, useGetAllAdmins } from '@/hooks/useGetAllAdmins';
import { AdminType } from '@/types/administrator';
import { useAdministratorTranslations } from '@/hooks/useAdministratorTranslations';
import { DocumentHead } from '@/components/core/DocumentHead';
import { useAuth } from '@/stores/auth';
import { hasAnyDepartmentWriteAccess } from '@/helpers/departmentPermissions';

export const Route = createFileRoute('/$tenantId/_layout/administrators/')({
  component: Administrators,
  validateSearch: (search: Record<string, unknown>): SearchFilterParams => {
    return {
      query: search.query as string,
      filter: search.filter as string,
      sort: search.sort as string,
      direction: search.direction as SortDirection,
      adminTypeFilter: search.adminTypeFilter as string[],
    };
  },
  beforeLoad: ({ params }) => {
    if (checkUserPermissions({ permission: 'tenant:viewer' })) {
      throw redirect({ to: '/$tenantId', params });
    }
  },
});

function Administrators() {
  const { query, sort, direction, adminTypeFilter } = Route.useSearch();

  const { admins, isLoading } = useGetAllAdmins();
  const { userProfile } = useAuth();

  const getIcon = useAscOrDescIcon();
  const { formatMessage } = useIntl();
  const getAdminTypeTranslation = useAdministratorTranslations();

  const [addAdminOpen, setAddAdminOpen] = useState<boolean>(false);
  const [selectedAdminTypes, setSelectedAdminTypes] = useState<OptionsType[]>(
    [],
  );
  const [announcement, setAnnouncement] = useState('');

  const sortConfigs = useMemo(() => {
    return createSortConfig<ExtendedUserDTO>({
      textFields: ['firstname', 'lastname'],
    });
  }, []);

  const adminTypeOptions: OptionsType[] = useMemo(() => {
    const allTypes: AdminType[] = [
      'orgAdmin',
      'orgBearerManager',
      'orgReader',
      'deptUser',
    ];
    return allTypes.map((type) => ({
      id: type,
      label: getAdminTypeTranslation(type),
    }));
  }, [getAdminTypeTranslation]);

  const filteredAdmins = useMemo(() => {
    if (selectedAdminTypes.length === 0) {
      return admins;
    }
    const selectedTypeIds = selectedAdminTypes.map((type) => type.id);
    return admins.filter((admin) => selectedTypeIds.includes(admin.adminType));
  }, [admins, selectedAdminTypes]);

  const {
    filterSearch,
    sortedItems,
    setFilterSearch,
    handleSort,
    handleSearch,
    handleSearchParams,
  } = useSearchFilterSort<ExtendedUserDTO>({
    items: filteredAdmins,
    searchKeys: ['firstname', 'lastname', 'email'],
    sortConfigs,
    routePath: Route.fullPath,
    defaultSearchParams: {
      sort: 'firstname',
      direction: 'asc',
      adminTypeFilter: [],
    },
  });

  const renderAdministratorItem = useCallback(
    (administrator: ExtendedUserDTO) => {
      return (
        <AdministratorListItem
          key={administrator.id}
          administrator={administrator}
          departments={administrator.departments}
          currentUserEmail={userProfile?.email}
        />
      );
    },
    [userProfile?.email],
  );

  const handleAdminTypeChange = useCallback(
    (options: OptionsType[]) => {
      setSelectedAdminTypes(options);
      const filterTypes = options.map((opt) => ({
        id: opt.id,
        label: String(opt.label),
      }));
      setFilterSearch((prev) => ({
        ...prev!,
        adminTypeFilter: filterTypes,
      }));
      handleSearchParams({ adminTypeFilter: options.map((opt) => opt.id) });
    },
    [setFilterSearch, handleSearchParams],
  );

  // Announce changes to sort and filter for screen readers
  useEffect(() => {
    if (!filterSearch || !sortedItems) return;

    const count = sortedItems.length;
    const sortLabel =
      filterSearch.sort === 'firstname'
        ? formatMessage({ defaultMessage: 'förnamn' })
        : formatMessage({ defaultMessage: 'efternamn' });
    const directionLabel =
      filterSearch.direction === 'asc'
        ? formatMessage({ defaultMessage: 'stigande' })
        : formatMessage({ defaultMessage: 'fallande' });

    const message = formatMessage(
      {
        defaultMessage:
          '{count} {count, plural, one {administratör} other {administratörer}} sorterade efter {sortLabel} {directionLabel}',
      },
      { count, sortLabel, directionLabel },
    );

    setAnnouncement(message);
  }, [filterSearch, sortedItems.length, formatMessage, sortedItems]);

  useEffect(() => {
    if (filterSearch) return;

    const adminTypes = Array.isArray(adminTypeFilter) ? adminTypeFilter : [];
    const filterTypes = adminTypes.map((typeId) => ({
      id: typeId,
      label: String(getAdminTypeTranslation(typeId as AdminType)),
    }));

    setFilterSearch({
      query: query ?? '',
      sort: sort ?? 'firstname',
      direction: direction ?? 'asc',
      adminTypeFilter: filterTypes,
    });
  }, [
    direction,
    filterSearch,
    query,
    setFilterSearch,
    sort,
    adminTypeFilter,
    getAdminTypeTranslation,
  ]);

  useEffect(() => {
    const typeFilters = filterSearch?.adminTypeFilter ?? [];
    const typeIds = Array.isArray(adminTypeFilter) ? adminTypeFilter : [];

    if (typeFilters.length > 0) {
      const options = adminTypeOptions.filter((opt) =>
        typeFilters.some((filter) => filter.id === opt.id),
      );
      setSelectedAdminTypes(options);
    } else if (typeIds.length > 0) {
      const options = adminTypeOptions.filter((opt) =>
        typeIds.includes(opt.id),
      );
      setSelectedAdminTypes(options);
    } else {
      setSelectedAdminTypes([]);
    }
  }, [filterSearch?.adminTypeFilter, adminTypeFilter, adminTypeOptions]);

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <DocumentHead
        titleKey="page.administrators.title"
        descriptionKey="page.administrators.description"
      />
      <Header
        title={<FormattedMessage defaultMessage="Administratörer" />}
        description={
          <FormattedMessage defaultMessage="Lista över administratörer" />
        }
        actions={
          hasAnyDepartmentWriteAccess() && (
            <ActionButtons>
              <Button
                startIcon={<IconPlus />}
                onClick={() => setAddAdminOpen(true)}
                variant="outline"
              >
                <FormattedMessage defaultMessage="Lägg till administratör" />
              </Button>
            </ActionButtons>
          )
        }
      />
      <FilteredListContainer>
        <div aria-live="polite" aria-atomic="true" className="sr-only">
          {announcement}
        </div>
        <RightPanel>
          {filterSearch && (
            <FilterPanel
              firstInput={
                <Input
                  type="text"
                  label={formatMessage({
                    defaultMessage: 'Sök',
                  })}
                  placeholder={formatMessage({
                    defaultMessage: 'Sök namn eller e-postadress',
                  })}
                  id="administrator-search"
                  value={filterSearch.query}
                  onChange={(e) => handleSearch(e.target.value)}
                  aria-description={formatMessage({
                    defaultMessage:
                      'Sök efter administratörer genom att skriva deras namn',
                  })}
                />
              }
              filterOptions={[
                <Dropdown
                  key="admin-type-filter"
                  id="admin-type-filter"
                  label={formatMessage({
                    defaultMessage: 'Typ av administratör',
                  })}
                  placeholder={formatMessage({
                    defaultMessage: 'Alla',
                  })}
                  options={adminTypeOptions}
                  value={selectedAdminTypes}
                  onValueChange={handleAdminTypeChange}
                  multiSelect={true}
                />,
              ]}
              sortingOptions={[
                <Button
                  key="administrator-first-name"
                  variant={
                    filterSearch.sort === 'firstname' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'firstname',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('firstname')}
                  aria-label={`Sortera efter förnamn ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'firstname'}
                >
                  <FormattedMessage defaultMessage="Förnamn" />
                </Button>,
                <Button
                  key="administrator-last-name"
                  variant={
                    filterSearch.sort === 'lastname' ? 'filled' : 'outline'
                  }
                  startIcon={getIcon({
                    selectedSort: filterSearch.sort!,
                    wantedSort: 'lastname',
                    sortDirection: filterSearch.direction!,
                  })}
                  onClick={() => handleSort('lastname')}
                  aria-label={`Sortera efter efternamn ${filterSearch.direction === 'asc' ? 'stigande' : 'fallande'}`}
                  aria-pressed={filterSearch.sort === 'lastname'}
                >
                  <FormattedMessage defaultMessage="Efternamn" />
                </Button>,
              ]}
            />
          )}
        </RightPanel>
        <VirtualizedList
          count={sortedItems?.length ?? 0}
          countName="administratörer"
          totalItems={sortedItems ?? []}
          filteredItems={sortedItems ?? []}
          renderItem={renderAdministratorItem}
          getItemKey={(administrator) => administrator.id}
        />
      </FilteredListContainer>
      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Lägg till administratör',
        })}
        content={
          <AddAdministratorDialog onClose={() => setAddAdminOpen(false)} />
        }
        onOpenChange={setAddAdminOpen}
        open={addAdminOpen}
      />
    </>
  );
}
